import { useState } from "react";
import {
    TextInput, Textarea, Select, Stack, Fieldset, Badge, Text, SimpleGrid, Input,
} from "@mantine/core";
import { UserSelect } from "../../components/UserSelect.js";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { classifyRisk, CiaLevel, RisikoCreate, RisikoStatus, Treatment, type Risiko } from "@iso27001/shared";
import { risikenApi, risikenLinks, loadFormOptions } from "../../api/risiken.js";
import { massnahmenApi } from "../../api/massnahmen.js";
import { ApiError } from "../../api/client.js";
import { LinkedItemsList } from "../../components/LinkedItemsList.js";
import { LinkPickerModal } from "../../components/LinkPickerModal.js";
import { EntityModal } from "../../components/EntityModal.js";
import { useRecordAccess } from "../../components/use-record-access.js";
import { isRisikoClosed } from "../../components/record-state.js";
import { risikoStatusColor, riskClassColor } from "../../components/status-colors.js";
import { AttachmentsSection } from "../../components/AttachmentsSection.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { useLocale } from "../../i18n/use-locale.js";
import { datePickerFormat } from "../../i18n/format-date.js";

interface Props {
    opened: boolean;
    onClose: () => void;
    initial: Risiko | null;
}

/** Truncate a string to `max` chars, appending "…" if cut. */
function truncate(s: string, max: number): string {
    return s.length <= max ? s : s.slice(0, max) + "…";
}

export function RisikoFormModal({ opened, onClose, initial }: Props) {
    const qc = useQueryClient();
    const isEdit = !!initial;
    const access = useRecordAccess(!!initial && isRisikoClosed(initial.status));
    const { readOnly } = access;
    const [pickerOpen, setPickerOpen] = useState(false);
    const { t } = useTranslation();
    const locale = useLocale();
    const { label } = useEnumLabel();
    const apiError = useApiErrorMessage();

    const statusOptions = RisikoStatus.options.map((value) => ({ value, label: label("risikoStatus", value) }));
    const treatmentOptions = Treatment.options.map((value) => ({ value, label: label("treatment", value) }));
    const ciaOptions = CiaLevel.options.map((value) => ({ value, label: label("ciaLevel", value) }));

    const optionsQuery = useQuery({
        queryKey: ["risiken-form-options"],
        queryFn: loadFormOptions,
        staleTime: 60_000,
    });
    const options = optionsQuery.data;

    const linkedMassnahmenQuery = useQuery({
        queryKey: ["risiken", initial?.id, "massnahmen"],
        queryFn: () => risikenLinks.listMassnahmen(initial!.id),
        enabled: !!initial?.id,
    });
    const linkedMassnahmen = linkedMassnahmenQuery.data?.items ?? [];

    const form = useForm({
        initialValues: {
            code: initial?.code ?? "",
            status: initial?.status ?? "Identifiziert",
            // identifiedAt stored as Date object for DateInput
            identifiedAt: initial?.identifiedAt ? new Date(initial.identifiedAt * 1000) : new Date() as Date | null,
            assetOwnerUserId: initial?.assetOwnerUserId ?? null as number | null,
            primaryAssetId: initial?.primaryAssetId ?? null as number | null,
            primaryAssetText: initial?.primaryAssetText ?? "",
            secondaryAssetId: initial?.secondaryAssetId ?? null as number | null,
            secondaryAssetText: initial?.secondaryAssetText ?? "",
            affectedParty: initial?.affectedParty ?? "",
            ciaConfidentiality: initial?.ciaConfidentiality ?? null as string | null,
            ciaIntegrity: initial?.ciaIntegrity ?? null as string | null,
            ciaAvailability: initial?.ciaAvailability ?? null as string | null,
            ciaAuthenticity: initial?.ciaAuthenticity ?? null as string | null,
            bedrohungId: initial?.bedrohungId ?? null as number | null,
            threatDescription: initial?.threatDescription ?? "",
            likelihoodBeforeId: initial?.likelihoodBeforeId ?? null as number | null,
            damageBeforeId: initial?.damageBeforeId ?? null as number | null,
            likelihoodReasoning: initial?.likelihoodReasoning ?? "",
            damageReasoning: initial?.damageReasoning ?? "",
            treatment: initial?.treatment ?? null as string | null,
            riskOwnerUserId: initial?.riskOwnerUserId ?? null as number | null,
            acceptanceReasoning: initial?.acceptanceReasoning ?? "",
            annexAControls: initial?.annexAControls ?? "",
            likelihoodAfterId: initial?.likelihoodAfterId ?? null as number | null,
            damageAfterId: initial?.damageAfterId ?? null as number | null,
        },
    });

    // Live risk score/class computation for "before" section
    const lBefore = options?.likelihoodLevels.find(l => l.id === form.values.likelihoodBeforeId);
    const dBefore = options?.damageLevels.find(d => d.id === form.values.damageBeforeId);
    const scoreBefore = lBefore && dBefore ? lBefore.numericValue * dBefore.numericValue : null;
    const classBefore = classifyRisk(scoreBefore);

    // Live risk score/class computation for "after" section
    const lAfter = options?.likelihoodLevels.find(l => l.id === form.values.likelihoodAfterId);
    const dAfter = options?.damageLevels.find(d => d.id === form.values.damageAfterId);
    const scoreAfter = lAfter && dAfter ? lAfter.numericValue * dAfter.numericValue : null;
    const classAfter = classifyRisk(scoreAfter);

    const mut = useMutation({
        mutationFn: async (vals: typeof form.values) => {
            const payload: RisikoCreate = {
                // convert Date back to unix seconds
                identifiedAt: vals.identifiedAt ? Math.floor(vals.identifiedAt.getTime() / 1000) : Math.floor(Date.now() / 1000),
                status: vals.status as Risiko["status"],
                assetOwnerUserId: vals.assetOwnerUserId ?? null,
                primaryAssetId: vals.primaryAssetId ?? null,
                primaryAssetText: vals.primaryAssetText || null,
                secondaryAssetId: vals.secondaryAssetId ?? null,
                secondaryAssetText: vals.secondaryAssetText || null,
                affectedParty: vals.affectedParty || null,
                ciaConfidentiality: (vals.ciaConfidentiality as Risiko["ciaConfidentiality"]) ?? null,
                ciaIntegrity: (vals.ciaIntegrity as Risiko["ciaIntegrity"]) ?? null,
                ciaAvailability: (vals.ciaAvailability as Risiko["ciaAvailability"]) ?? null,
                ciaAuthenticity: (vals.ciaAuthenticity as Risiko["ciaAuthenticity"]) ?? null,
                bedrohungId: vals.bedrohungId ?? null,
                threatDescription: vals.threatDescription || null,
                likelihoodBeforeId: vals.likelihoodBeforeId ?? null,
                damageBeforeId: vals.damageBeforeId ?? null,
                likelihoodReasoning: vals.likelihoodReasoning || null,
                damageReasoning: vals.damageReasoning || null,
                treatment: (vals.treatment as Risiko["treatment"]) ?? null,
                riskOwnerUserId: vals.riskOwnerUserId ?? null,
                acceptanceReasoning: vals.acceptanceReasoning || null,
                annexAControls: vals.annexAControls.trim() || null,
                likelihoodAfterId: vals.likelihoodAfterId ?? null,
                damageAfterId: vals.damageAfterId ?? null,
            };
            // Include code only when editing or when user explicitly provided one on create
            if (isEdit || vals.code.trim() !== "") {
                payload.code = vals.code;
            }
            return isEdit
                ? risikenApi.update(initial!.id, payload)
                : risikenApi.create(payload);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["risiken"] });
            notifications.show({ message: isEdit ? t("risiken.form.notify.updated") : t("risiken.form.notify.created"), color: "teal" });
            onClose();
        },
        onError: (e) => {
            if (e instanceof ApiError && e.code === "Conflict") {
                form.setFieldError("code", t("risiken.error.codeConflict"));
            } else {
                notifications.show({ message: apiError(e, { fallback: "risiken.error.saveFailed" }), color: "red" });
            }
        },
    });

    const assetOptions = (options?.assets ?? []).map(a => ({ value: String(a.id), label: `${a.code} — ${a.name}` }));
    const bedrohungOptions = (options?.bedrohungen ?? []).map(b => ({ value: String(b.id), label: `${b.code} — ${b.name}` }));
    const likelihoodOptions = (options?.likelihoodLevels ?? []).map(l => ({ value: String(l.id), label: `${l.label} (${l.numericValue})` }));
    const damageOptions = (options?.damageLevels ?? []).map(d => ({ value: String(d.id), label: `${d.label} (${d.numericValue})` }));

    // A level pair and its score, laid out as one row: likelihood, damage, and
    // the resulting score/class in the third column where it reads as a result.
    const scoreField = (labelKey: "risiken.form.scoreBefore.label" | "risiken.form.scoreAfter.label", score: number | null, cls: ReturnType<typeof classifyRisk>) => (
        <Input.Wrapper label={t(labelKey)}>
            <div style={{ minHeight: 36, display: "flex", alignItems: "center" }}>
                {score !== null && cls !== null ? (
                    <Badge color={riskClassColor[cls]} size="lg">
                        {score} · {label("riskClass", cls)}
                    </Badge>
                ) : (
                    <Text size="sm" c="dimmed">—</Text>
                )}
            </div>
        </Input.Wrapper>
    );

    const levelSelect = (
        field: "likelihoodBeforeId" | "damageBeforeId" | "likelihoodAfterId" | "damageAfterId",
        labelText: string,
        data: { value: string; label: string }[],
    ) => (
        <Select
            label={labelText}
            data={data}
            readOnly={readOnly}
            clearable={!readOnly}
            placeholder={t("risiken.field.levelPlaceholder")}
            value={form.values[field] !== null ? String(form.values[field]) : null}
            onChange={(val) => form.setFieldValue(field, val ? Number(val) : null)}
            error={form.errors[field]}
        />
    );

    return (
        <EntityModal
            opened={opened}
            onClose={onClose}
            size="xl"
            title={isEdit ? t("risiken.form.title") : t("risiken.newRisk")}
            code={initial?.code}
            badge={initial && <Badge color={risikoStatusColor[initial.status]} variant="light">{label("risikoStatus", initial.status)}</Badge>}
            readOnly={readOnly}
            closed={access.closed}
            onUnlock={access.unlock}
            onSubmit={form.onSubmit((vals) => mut.mutate(vals))}
            saving={mut.isPending}
            submitLabel={isEdit ? t("common.action.save") : t("common.action.createSubmit")}
        >
            <Stack>
                {/* Read-only drops the code input (it is in the title), leaving three short values. */}
                <SimpleGrid cols={{ base: 1, sm: readOnly ? 3 : 2 }}>
                    {!readOnly && (
                        <TextInput
                            label={isEdit ? t("common.field.code") : t("common.field.codeOptional")}
                            description={isEdit ? undefined : t("risiken.form.code.autoHint")}
                            // Hint under the input, so the field lines up with its neighbours in the row.
                            inputWrapperOrder={["label", "input", "description", "error"]}
                            required={isEdit}
                            maxLength={80}
                            placeholder={isEdit ? undefined : t("common.field.codeAutoPlaceholder")}
                            {...form.getInputProps("code")}
                        />
                    )}
                    <Select label={t("common.field.status")} data={statusOptions} readOnly={readOnly} {...form.getInputProps("status")} />
                    <DateInput
                        label={t("risiken.form.identifiedAt.label")}
                        required
                        readOnly={readOnly}
                        placeholder={t("common.field.datePlaceholder")}
                        valueFormat={datePickerFormat(locale)}
                        value={form.values.identifiedAt}
                        onChange={(val) => form.setFieldValue("identifiedAt", val)}
                        error={form.errors.identifiedAt}
                    />
                    <UserSelect
                        label={t("risiken.form.assetOwner.label")}
                        placeholder={t("common.field.userPlaceholder")}
                        readOnly={readOnly}
                        clearable={!readOnly}
                        value={form.values.assetOwnerUserId}
                        onChange={(val) => form.setFieldValue("assetOwnerUserId", val)}
                        error={form.errors.assetOwnerUserId}
                    />
                </SimpleGrid>

                {/* Section 1: Identifizierung */}
                <Fieldset legend={t("risiken.form.section.identification")}>
                    <Stack>
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                            <Select
                                label={t("risiken.form.primaryAsset.label")}
                                data={assetOptions}
                                searchable={!readOnly}
                                readOnly={readOnly}
                                clearable={!readOnly}
                                placeholder={t("risiken.field.assetPlaceholder")}
                                value={form.values.primaryAssetId !== null ? String(form.values.primaryAssetId) : null}
                                onChange={(val) => form.setFieldValue("primaryAssetId", val ? Number(val) : null)}
                                error={form.errors.primaryAssetId}
                            />
                            <TextInput
                                label={t("risiken.form.primaryAssetText.label")}
                                placeholder={t("risiken.field.freitextPlaceholder")}
                                readOnly={readOnly}
                                maxLength={500}
                                {...form.getInputProps("primaryAssetText")}
                            />
                            <Select
                                label={t("risiken.form.secondaryAsset.label")}
                                data={assetOptions}
                                searchable={!readOnly}
                                readOnly={readOnly}
                                clearable={!readOnly}
                                placeholder={t("risiken.field.assetPlaceholder")}
                                value={form.values.secondaryAssetId !== null ? String(form.values.secondaryAssetId) : null}
                                onChange={(val) => form.setFieldValue("secondaryAssetId", val ? Number(val) : null)}
                                error={form.errors.secondaryAssetId}
                            />
                            <TextInput
                                label={t("risiken.form.secondaryAssetText.label")}
                                placeholder={t("risiken.field.freitextPlaceholder")}
                                readOnly={readOnly}
                                maxLength={500}
                                {...form.getInputProps("secondaryAssetText")}
                            />
                        </SimpleGrid>
                        <Textarea
                            label={t("risiken.form.affectedParty.label")}
                            readOnly={readOnly}
                            autosize
                            minRows={readOnly ? 1 : 2}
                            maxLength={2000}
                            {...form.getInputProps("affectedParty")}
                        />
                    </Stack>
                </Fieldset>

                {/* Section 2: Schutzbedarfsanalyse */}
                <Fieldset legend={t("risiken.form.section.protectionNeeds")}>
                    <SimpleGrid cols={{ base: 2, sm: 4 }}>
                        <Select label={t("risiken.form.cia.confidentiality")} data={ciaOptions} readOnly={readOnly} clearable={!readOnly} {...form.getInputProps("ciaConfidentiality")} />
                        <Select label={t("risiken.form.cia.integrity")} data={ciaOptions} readOnly={readOnly} clearable={!readOnly} {...form.getInputProps("ciaIntegrity")} />
                        <Select label={t("risiken.form.cia.availability")} data={ciaOptions} readOnly={readOnly} clearable={!readOnly} {...form.getInputProps("ciaAvailability")} />
                        <Select label={t("risiken.form.cia.authenticity")} data={ciaOptions} readOnly={readOnly} clearable={!readOnly} {...form.getInputProps("ciaAuthenticity")} />
                    </SimpleGrid>
                </Fieldset>

                {/* Section 3: Bedrohung & Bewertung vor Behandlung */}
                <Fieldset legend={t("risiken.form.section.threatAssessment")}>
                    <Stack>
                        <Select
                            label={t("risiken.field.threat")}
                            data={bedrohungOptions}
                            searchable={!readOnly}
                            readOnly={readOnly}
                            clearable={!readOnly}
                            placeholder={t("risiken.field.threatPlaceholder")}
                            value={form.values.bedrohungId !== null ? String(form.values.bedrohungId) : null}
                            onChange={(val) => form.setFieldValue("bedrohungId", val ? Number(val) : null)}
                            error={form.errors.bedrohungId}
                        />
                        <Textarea
                            label={t("risiken.form.threatDescription.label")}
                            readOnly={readOnly}
                            autosize
                            minRows={readOnly ? 1 : 2}
                            maxLength={5000}
                            {...form.getInputProps("threatDescription")}
                        />
                        <SimpleGrid cols={{ base: 1, sm: 3 }}>
                            {levelSelect("likelihoodBeforeId", t("risiken.field.likelihood"), likelihoodOptions)}
                            {levelSelect("damageBeforeId", t("risiken.field.damage"), damageOptions)}
                            {scoreField("risiken.form.scoreBefore.label", scoreBefore, classBefore)}
                        </SimpleGrid>
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                            <Textarea
                                label={t("risiken.form.likelihoodReasoning.label")}
                                readOnly={readOnly}
                                autosize
                                minRows={readOnly ? 1 : 2}
                                maxLength={5000}
                                {...form.getInputProps("likelihoodReasoning")}
                            />
                            <Textarea
                                label={t("risiken.form.damageReasoning.label")}
                                readOnly={readOnly}
                                autosize
                                minRows={readOnly ? 1 : 2}
                                maxLength={5000}
                                {...form.getInputProps("damageReasoning")}
                            />
                        </SimpleGrid>
                    </Stack>
                </Fieldset>

                {/* Section 4: Risikobehandlung */}
                <Fieldset legend={t("risiken.form.section.treatment")}>
                    <Stack>
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                            <Select label={t("risiken.field.treatment")} data={treatmentOptions} readOnly={readOnly} clearable={!readOnly} {...form.getInputProps("treatment")} />
                            <UserSelect
                                label={t("risiken.field.riskOwner")}
                                placeholder={t("common.field.userPlaceholder")}
                                readOnly={readOnly}
                                clearable={!readOnly}
                                value={form.values.riskOwnerUserId}
                                onChange={(val) => form.setFieldValue("riskOwnerUserId", val)}
                                error={form.errors.riskOwnerUserId}
                            />
                        </SimpleGrid>
                        <Textarea
                            label={t("risiken.form.acceptanceReasoning.label")}
                            readOnly={readOnly}
                            autosize
                            minRows={readOnly ? 1 : 2}
                            maxLength={5000}
                            {...form.getInputProps("acceptanceReasoning")}
                        />
                        {/*
                          The controls treating this risk — what an auditor
                          follows from the register to the Statement of
                          Applicability. Free text on purpose: the references
                          arrive as lists like "A.5.19, A.6.2".
                        */}
                        <TextInput
                            label={t("risiken.form.annexAControls.label")}
                            placeholder={t("risiken.form.annexAControls.placeholder")}
                            readOnly={readOnly}
                            maxLength={2000}
                            {...form.getInputProps("annexAControls")}
                        />
                    </Stack>
                </Fieldset>

                {/* Section 5: Verknüpfte Maßnahmen — before the post-treatment assessment they lead to */}
                <Fieldset legend={t("risiken.form.section.linkedMassnahmen")}>
                    {initial?.id ? (
                        <>
                            <LinkedItemsList
                                items={linkedMassnahmen.map((m) => ({
                                    id: m.id,
                                    label: `${m.code} — ${truncate(m.description, 60)}`,
                                }))}
                                onUnlink={async (id) => {
                                    await risikenLinks.unlink(initial.id, id);
                                    qc.invalidateQueries({ queryKey: ["risiken", initial.id, "massnahmen"] });
                                }}
                                onAddClick={() => setPickerOpen(true)}
                                emptyText={t("risiken.form.massnahmen.emptyText")}
                                canEdit={!readOnly}
                            />
                            <LinkPickerModal
                                opened={pickerOpen}
                                onClose={() => setPickerOpen(false)}
                                title={t("risiken.form.massnahmen.linkModalTitle")}
                                loadCandidates={(q) => massnahmenApi.list({ q, pageSize: 200 }).then((r) => r.items)}
                                excludeIds={linkedMassnahmen.map((m) => m.id)}
                                labelOf={(m) => `${m.code} — ${truncate(m.description, 80)}`}
                                idOf={(m) => m.id}
                                onPick={async (id) => {
                                    await risikenLinks.link(initial.id, id);
                                    qc.invalidateQueries({ queryKey: ["risiken", initial.id, "massnahmen"] });
                                }}
                            />
                        </>
                    ) : (
                        <Text size="sm" c="dimmed">{t("risiken.form.massnahmen.linkAfterSaveHint")}</Text>
                    )}
                </Fieldset>

                {/* Section 6: Bewertung nach Behandlung */}
                <Fieldset legend={t("risiken.form.section.postTreatmentAssessment")}>
                    <SimpleGrid cols={{ base: 1, sm: 3 }}>
                        {levelSelect("likelihoodAfterId", t("risiken.field.likelihood"), likelihoodOptions)}
                        {levelSelect("damageAfterId", t("risiken.field.damage"), damageOptions)}
                        {scoreField("risiken.form.scoreAfter.label", scoreAfter, classAfter)}
                    </SimpleGrid>
                </Fieldset>

                {initial?.id && (
                    <Fieldset legend={t("attachments.sectionTitle")}>
                        <AttachmentsSection entity="risiko" entityId={initial.id} canEdit={!readOnly} />
                    </Fieldset>
                )}
            </Stack>
        </EntityModal>
    );
}
